import React, { FC, ChangeEvent, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import styles from './NumberInput.module.scss';

import getClassNames from '../../utils/getClassNames';
import {
  increment, decrement, normalize, parse, format,
} from './helpers';

const sizes = [
  'm',
  'l',
] as const;

type Size = typeof sizes[number];

export type NumberInputProps = {
  /**
   * Optional id that will be assigned to the input element
   */
  id?: string;
  inputNumRef?: React.Ref<HTMLInputElement>;
  value: number;
  min: number;
  max: number;
  step?: number;
  'data-test'?: string;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onButtonKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  classNameModifier?: string;
  buttonsTabIndex?: number;
  autoFocus?: boolean;
  size?: Size;
  /** Sets input border-color to red */
  error?: boolean;
  /** Keep the buttons small even in mobile sizes. Also reduces the internal padding */
  smallButtonsOnMobile?: boolean;
};

const NumberInput: FC<NumberInputProps> = ({
  id,
  inputNumRef,
  min,
  max,
  step = 1,
  value,
  onChange,
  onBlur,
  onKeyDown,
  onButtonKeyDown,
  'data-test': dataTest = '',
  classNameModifier,
  buttonsTabIndex = 0,
  autoFocus = false,
  size = 'm',
  error,
  smallButtonsOnMobile,
}) => {
  const [t] = useTranslation('common');
  // The presentation value allows emptying the field. In that case, the actual value shows
  // as a placeholder
  const [presentationValue, setPresentationValue] = useState<string>(format(value));

  const plusClick = () => {
    // Use form value
    const val = increment(Number(value), step);
    setPresentationValue(format(val));
    onChange(val);
  };

  const minusClick = () => {
    // Use form value
    const val = decrement(Number(value), step);
    setPresentationValue(format(val));
    onChange(val);
  };

  const onDirectEntry = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: rawValue } = event.target;
    if (!rawValue) {
      setPresentationValue('');
      return;
    }
    const numberValue = parse(rawValue);
    const normalized = normalize(numberValue, min, max);
    setPresentationValue(format(normalized));
    onChange(normalized);
  };

  return (
    <div className={getClassNames(styles, 'root', classNames(classNameModifier, { smallButtonsOnMobile }))}>
      <button
        type="button"
        disabled={value <= min}
        onClick={minusClick}
        className={getClassNames(styles, 'button', classNames(classNameModifier, { smallButtonsOnMobile }))}
        aria-label={t('number-input--decrease-aria-label')}
        aria-describedby={id ? `${id}-decrease-desc` : undefined}
        tabIndex={buttonsTabIndex}
        onKeyDown={onButtonKeyDown}
        data-test={withSuffix(dataTest, 'minus-button')}
        onBlur={onBlur}
      >
        <span className={styles.buttonHit} />
        <svg width="12" height="3" viewBox="0 0 12 3" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 1.5C12 1.91421 11.6642 2.25 11.25 2.25H0.75C0.335786 2.25 0 1.91421 0 1.5V1.5C0 1.08579 0.335786 0.75 0.75 0.75H11.25C11.6642 0.75 12 1.08579 12 1.5V1.5Z" fill="currentColor" />
        </svg>
        <span id={id ? `${id}-decrease-desc` : undefined} className={styles.screenReaderOnly}>
          {t('number-input--decrease-aria-desc')}
          {' '}
          {step}
        </span>
      </button>
      <input
        id={id}
        ref={inputNumRef}
        min={min}
        max={max}
        step={step}
        value={presentationValue}
        onChange={onDirectEntry}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        type="number"
        pattern="\d*"
        className={getClassNames(styles, 'input', classNames(classNameModifier, `body-${size}`, { error }))}
        // Shows what value is being used for calculations (this value is in form state)
        placeholder={format(value)}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        data-test={withSuffix(dataTest, 'textbox')}
      />
      <button
        type="button"
        disabled={step > 1 ? value + step >= max : value >= max}
        onClick={plusClick}
        className={getClassNames(styles, 'button', classNames(classNameModifier, { smallButtonsOnMobile }))}
        aria-label={t('number-input--increase-aria-label')}
        aria-describedby={id ? `${id}-increase-desc` : undefined}
        tabIndex={buttonsTabIndex}
        onKeyDown={onButtonKeyDown}
        data-test={withSuffix(dataTest, 'plus-button')}
        onBlur={onBlur}
      >
        <span className={styles.buttonHit} />
        {/* <Icon id="plus" size="s" /> */}
        <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
          <path d="M6 0.5C6.41421 0.5 6.75 0.835786 6.75 1.25V5.75H11.25C11.6642 5.75 12 6.08579 12 6.5C12 6.91421 11.6642 7.25 11.25 7.25H6.75V11.75C6.75 12.1642 6.41421 12.5 6 12.5C5.58579 12.5 5.25 12.1642 5.25 11.75V7.25H0.75C0.335786 7.25 0 6.91421 0 6.5C0 6.08579 0.335786 5.75 0.75 5.75H5.25V1.25C5.25 0.835786 5.58579 0.5 6 0.5Z" fill="currentColor" />
        </svg>
        <span id={id ? `${id}-increase-desc` : undefined} className={styles.screenReaderOnly}>
          {t('number-input--increase-aria-desc')}
          {' '}
          {step}
        </span>
      </button>
    </div>
  );
};

export default NumberInput;
